/* "View in your room" (Shalev 2026-09-26, The Poster Club's button; theirs is the paid Blankwall app).
   Phone: builds the canvas as a small 3D model at the size and frame the buyer has picked, and hands it to
   Google's <model-viewer>, which opens the camera and hangs it on the wall (ARCore via WebXR on Android,
   Quick Look on iPhone, where model-viewer turns the scene into a USDZ itself). Computer: a QR code that
   opens this same page on the phone with the viewer already open (?vir=1), like theirs.

   The artwork is the product's flat file (metafield custom.flat_art), never a mockup. The model is built
   here, in the browser, so every size and frame needs no file of its own: a box as deep as a stretched
   canvas (1.5 in) with the picture on the front and its edges wrapped round the sides, and for a framed
   variant a floating frame 1.25 in wide around it with a 0.25 in shadow gap. */
(() => {
  const btn = document.querySelector('[data-vir]');
  const dlg = document.querySelector('[data-vir-dialog]');
  if (!btn || !dlg) return;
  if (!btn.dataset.art) { btn.hidden = true; return; }
  /* Absolute: the model is a blob: document, and a relative /cdn/... path cannot resolve against it. */
  const art = new URL(btn.dataset.art, location.href).href;

  const IN = 0.0254;
  const FRAME_RGB = { black: [0.07, 0.07, 0.07], espresso: [0.2, 0.13, 0.1], natural: [0.72, 0.56, 0.36], white: [0.93, 0.92, 0.9] };
  const phone = matchMedia('(pointer: coarse)').matches;

  const choice = () => {
    let size = '24 x 36 in', frame = 'Stretched Canvas';
    document.querySelectorAll('fieldset[data-option] input:checked').forEach((i) => {
      if (/\d+\s*x\s*\d+/.test(i.value)) size = i.value; else frame = i.value;
    });
    const m = size.match(/(\d+)\s*x\s*(\d+)/);
    return { w: +m[1], h: +m[2], size, frame };
  };

  /* One glTF box per part: 24 vertices (4 per face) so each face has its own normal and UVs. */
  function box(x0, x1, y0, y1, z0, z1, uvFront) {
    const P = [], N = [], T = [], I = [];
    const face = (a, b, c, d, n, uv) => {
      const k = P.length / 3;
      [a, b, c, d].forEach((v) => P.push(...v)); for (let i = 0; i < 4; i++) N.push(...n);
      T.push(...uv); I.push(k, k + 1, k + 2, k, k + 2, k + 3);
    };
    const [u0, v0, u1, v1] = uvFront;
    const e = 0.004; /* the side faces sample a thin strip of the picture's own edge: a wrapped canvas */
    face([x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1], [0, 0, 1], [u0, v1, u1, v1, u1, v0, u0, v0]);
    face([x1, y0, z0], [x0, y0, z0], [x0, y1, z0], [x1, y1, z0], [0, 0, -1], [0, 0, 0, 0, 0, 0, 0, 0]);
    face([x1, y0, z1], [x1, y0, z0], [x1, y1, z0], [x1, y1, z1], [1, 0, 0], [u1 - e, v1, u1, v1, u1, v0, u1 - e, v0]);
    face([x0, y0, z0], [x0, y0, z1], [x0, y1, z1], [x0, y1, z0], [-1, 0, 0], [u0, v1, u0 + e, v1, u0 + e, v0, u0, v0]);
    face([x0, y1, z1], [x1, y1, z1], [x1, y1, z0], [x0, y1, z0], [0, 1, 0], [u0, v0 + e, u1, v0 + e, u1, v0, u0, v0]);
    face([x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [x0, y0, z1], [0, -1, 0], [u0, v1, u1, v1, u1, v1 - e, u0, v1 - e]);
    return { P, N, T, I };
  }

  function gltf(c, imgW, imgH) {
    const W = c.w * IN, H = c.h * IN, D = 1.5 * IN;
    /* Cover-crop the artwork to the size's ratio, centred, as the print files are cut. */
    const ra = imgW / imgH, rs = c.w / c.h;
    let u0 = 0, u1 = 1, v0 = 0, v1 = 1;
    if (ra > rs) { const k = rs / ra; u0 = (1 - k) / 2; u1 = 1 - u0; } else { const k = ra / rs; v0 = (1 - k) / 2; v1 = 1 - v0; }
    const parts = [{ g: box(-W / 2, W / 2, 0, H, 0.02 * IN, D, [u0, v0, u1, v1]), mat: 0 }];
    const key = c.frame.toLowerCase().split(' ')[0];
    if (FRAME_RGB[key]) {
      const f = 1.25 * IN, gap = 0.25 * IN, FD = D + 0.3 * IN;
      const X0 = -W / 2 - gap, X1 = W / 2 + gap, Y0 = -gap, Y1 = H + gap;
      parts.push({ g: box(X0 - f, X1 + f, Y1, Y1 + f, 0, FD, [0, 0, 0, 0]), mat: 1 });
      parts.push({ g: box(X0 - f, X1 + f, Y0 - f, Y0, 0, FD, [0, 0, 0, 0]), mat: 1 });
      parts.push({ g: box(X0 - f, X0, Y0, Y1, 0, FD, [0, 0, 0, 0]), mat: 1 });
      parts.push({ g: box(X1, X1 + f, Y0, Y1, 0, FD, [0, 0, 0, 0]), mat: 1 });
      parts.push({ g: box(X0, X1, Y0, Y1, 0, 0.01 * IN, [0, 0, 0, 0]), mat: 2 }); /* the dark floor of the gap */
    }

    const bufs = [], views = [], accessors = [], meshes = [];
    let off = 0;
    const add = (arr, Type, target, comps, type, minmax) => {
      const a = new Type(arr), bytes = new Uint8Array(a.buffer);
      bufs.push(bytes); const pad = (4 - (bytes.length % 4)) % 4; if (pad) bufs.push(new Uint8Array(pad));
      views.push({ buffer: 0, byteOffset: off, byteLength: bytes.length, target });
      off += bytes.length + pad;
      const acc = { bufferView: views.length - 1, componentType: comps, count: arr.length / ({ VEC3: 3, VEC2: 2, SCALAR: 1 })[type], type };
      if (minmax) Object.assign(acc, minmax);
      accessors.push(acc); return accessors.length - 1;
    };
    parts.forEach(({ g, mat }) => {
      const xs = [], ys = [], zs = []; for (let i = 0; i < g.P.length; i += 3) { xs.push(g.P[i]); ys.push(g.P[i + 1]); zs.push(g.P[i + 2]); }
      const p = add(g.P, Float32Array, 34962, 5126, 'VEC3', { min: [Math.min(...xs), Math.min(...ys), Math.min(...zs)], max: [Math.max(...xs), Math.max(...ys), Math.max(...zs)] });
      const n = add(g.N, Float32Array, 34962, 5126, 'VEC3');
      const t = add(g.T, Float32Array, 34962, 5126, 'VEC2');
      const i = add(g.I, Uint16Array, 34963, 5123, 'SCALAR');
      meshes.push({ primitives: [{ attributes: { POSITION: p, NORMAL: n, TEXCOORD_0: t }, indices: i, material: mat }] });
    });
    const all = new Uint8Array(off); let o = 0; bufs.forEach((b) => { all.set(b, o); o += b.length; });
    let bin = ''; for (let i = 0; i < all.length; i += 0x8000) bin += String.fromCharCode.apply(null, all.subarray(i, i + 0x8000));
    const rgb = FRAME_RGB[key] || [0, 0, 0];
    return {
      asset: { version: '2.0', generator: 'Melanin Art Gallery vir.js' },
      scene: 0, scenes: [{ nodes: meshes.map((_, k) => k) }],
      nodes: meshes.map((_, k) => ({ mesh: k })), meshes,
      materials: [
        { pbrMetallicRoughness: { baseColorTexture: { index: 0 }, metallicFactor: 0, roughnessFactor: 0.9 } },
        { pbrMetallicRoughness: { baseColorFactor: [...rgb, 1], metallicFactor: 0, roughnessFactor: 0.7 } },
        { pbrMetallicRoughness: { baseColorFactor: [0.05, 0.05, 0.05, 1], metallicFactor: 0, roughnessFactor: 1 } },
      ],
      textures: [{ source: 0, sampler: 0 }], samplers: [{ magFilter: 9729, minFilter: 9987, wrapS: 33071, wrapT: 33071 }],
      images: [{ uri: art }],
      buffers: [{ byteLength: off, uri: 'data:application/octet-stream;base64,' + btoa(bin) }],
      bufferViews: views, accessors,
    };
  }

  let mvLoaded = null;
  const loadMV = () => mvLoaded || (mvLoaded = new Promise((ok, no) => {
    const s = document.createElement('script'); s.type = 'module';
    s.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js';
    s.onload = ok; s.onerror = no; document.head.appendChild(s);
  }));
  const imgSize = () => new Promise((ok) => { const i = new Image(); i.crossOrigin = 'anonymous'; i.onload = () => ok([i.naturalWidth, i.naturalHeight]); i.onerror = () => ok([3, 4]); i.src = art; });

  let url = null;
  async function openPhone() {
    const c = choice();
    dlg.querySelector('[data-vir-size]').textContent = `${c.size} · ${c.frame}`;
    dlg.dataset.mode = 'phone';
    dlg.showModal();
    await loadMV();
    const [iw, ih] = await imgSize();
    if (url) URL.revokeObjectURL(url);
    url = URL.createObjectURL(new Blob([JSON.stringify(gltf(c, iw, ih))], { type: 'model/gltf+json' }));
    const mv = dlg.querySelector('model-viewer');
    mv.src = url;
  }

  let qrLoaded = null;
  const loadQR = () => qrLoaded || (qrLoaded = new Promise((ok, no) => {
    const s = document.createElement('script');
    s.src = 'https://cdnjs.cloudflare.com/ajax/libs/qrcode-generator/1.4.4/qrcode.min.js';
    s.onload = ok; s.onerror = no; document.head.appendChild(s);
  }));
  async function openDesk() {
    dlg.dataset.mode = 'desk';
    dlg.showModal();
    await loadQR();
    const link = new URL(location.href); link.searchParams.set('vir', '1');
    const q = window.qrcode(0, 'M'); q.addData(link.toString()); q.make();
    dlg.querySelector('[data-vir-qr]').innerHTML = q.createSvgTag({ cellSize: 5, margin: 0, scalable: true });
  }

  btn.addEventListener('click', () => (phone ? openPhone() : openDesk()));
  dlg.addEventListener('click', (e) => {
    if (e.target === dlg || e.target.closest('[data-vir-close]')) dlg.close();
    if (e.target.closest('[data-vir-go]')) { const mv = dlg.querySelector('model-viewer'); if (mv && mv.canActivateAR) mv.activateAR(); else dlg.querySelector('[data-vir-noar]').hidden = false; }
  });
  if (phone && new URLSearchParams(location.search).get('vir') === '1') openPhone();
})();
